
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, MailCheck, Loader2, Phone, MapPin, GraduationCap, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "../components/ui/use-toast";
import Logo from "../assets/fillopsLogo.png";
import AuthArtwork from "../assets/learning.jpeg";
import authSession from "../lib/authSession";
import { API_URL } from "../constants";
import { toDisplayString, getErrorMessage } from "../utils/toDisplayString";
import { safeNext } from "../lib/roles";

// ── Validation ───────────────────────────────────────────────────
const schema = z
  .object({
    email:           z.string().email("Invalid email address"),
    first_name:      z.string().min(1, "First name is required"),
    last_name:       z.string().min(1, "Last name is required"),
    address:         z.string().optional(),
    city:            z.string().optional(),
    qualification:   z.string().optional(),
    field:           z.string().optional(),
    password:        z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(8, "Please confirm your password"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// ── Email Verification Notice ────────────────────────────────────
const EmailVerificationNotice = ({ email, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7 }}
    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg rounded-2xl p-6 text-center w-full max-w-md"
  >
    <MailCheck className="mx-auto h-12 w-12 text-indigo-600" />
    <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-3">Verify Your Email</h2>
    <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm leading-relaxed">
      We've sent a verification link to:
    </p>
    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mt-1">{email}</p>
    <p className="text-gray-500 dark:text-gray-400 text-xs mt-4">
      Please check your inbox or spam folder and click the link to activate your account.
    </p>
    {onContinue && (
      <button
        type="button"
        onClick={onContinue}
        className="mt-5 w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
      >
        Continue to verification
      </button>
    )}
  </motion.div>
);

const defaultValues = {
  email: "", first_name: "", last_name: "",
  address: "", city: "", qualification: "", field: "",
  password: "", confirmPassword: "",
};
const RETURN_TO_KEY = "fillop_auth_return_to";

// ── Main Component ───────────────────────────────────────────────
const Register = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const location       = useLocation();
  const { toast }      = useToast();
  const returnTo       = safeNext(searchParams.get("next"));

  const [showPassword,     setShowPassword]     = useState(false);
  const [showConfirm,      setShowConfirm]      = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [showVerifyNotice, setShowVerifyNotice] = useState(false);
  const [registeredEmail,  setRegisteredEmail]  = useState("");
  const [error,            setError]            = useState("");

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => { reset(defaultValues); }, [reset]);
  useEffect(() => { if (returnTo) localStorage.setItem(RETURN_TO_KEY, returnTo); }, [returnTo]);

  // Handle ?verified and ?error URL params
  useEffect(() => {
    const verified   = searchParams.get("verified");
    const errorParam = searchParams.get("error");
    const reason     = searchParams.get("reason");

    if (verified === "true") {
      reset(defaultValues);
      toast({ title: "Email Verified 🎉", description: "You can now log in to your account." });
      navigate(location.pathname, { replace: true });
    }

    if (errorParam) {
      reset(defaultValues);
      let title       = "Error";
      let description = "Your verification link is invalid or has expired.";
      if (errorParam === "verification_failed") {
        title = "Verification Failed";
        description = reason === "invalid-or-expired"
          ? "Your verification link is invalid or has expired. Please request a new one."
          : reason
            ? reason.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
            : description;
      }
      toast({ variant: "destructive", title: `${title} ❌`, description });
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, reset, toast, navigate, location]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      // POST /api/auth/register — contract: { email, password, name, phone }.
      const payload = {
        email: data.email,
        password: data.password,
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        ...(data.phone ? { phone: data.phone } : {}),
      };

      const response = await authSession.authFetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (!response.ok || result.success === false || result.error) {
        const message = toDisplayString(
          result.message || result.error,
          "Registration failed. Please try again."
        );
        setError(message);
        toast({ variant: "destructive", title: "Registration failed", description: message });
        return;
      }

      // If the API auto-authenticates on register, go straight to the dashboard.
      const token = authSession.findAccessToken(result);
      if (token) {
        authSession.setSession({
          token,
          role: "user",
          user: result.user || result.data?.user || null,
        });
        localStorage.setItem("isAdmin", "false");
        localStorage.setItem("email", data.email);
        localStorage.setItem("uemail", data.email);
        localStorage.setItem("name", `${data.first_name} ${data.last_name || ""}`.trim());
        toast({ title: "Registration successful 🎉", description: "Welcome to Fillop." });
        localStorage.removeItem(RETURN_TO_KEY);
        navigate(returnTo || "/", { replace: true });
        return;
      }

      // Otherwise the account needs email verification first (link at /verify-email).
      localStorage.setItem("optEmail", data.email);
      localStorage.setItem("email", data.email);
      reset(defaultValues);
      setRegisteredEmail(data.email);
      setShowVerifyNotice(true);
      toast({ title: "Registration successful 🎉", description: "Verify your email to continue." });
    } catch (err) {
      console.error("Register error:", err);
      const message = getErrorMessage(err, "Could not connect to server. Please try again.");
      setError(message);
      toast({ variant: "destructive", title: "Registration failed", description: message });
    } finally {
      setLoading(false);
    }
  };

  if (showVerifyNotice) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-white dark:bg-background px-3 py-6">
        <EmailVerificationNotice
          email={registeredEmail}
          onContinue={() =>
            navigate(`/verify-email?email=${encodeURIComponent(registeredEmail)}${returnTo ? `&next=${encodeURIComponent(returnTo)}` : ""}`)
          }
        />
      </section>
    );
  }

  const inputCls = "w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition";

  return (
    <section className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-6 sm:px-6 lg:p-10 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mx-auto grid w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-2xl shadow-slate-900/10 lg:min-h-[680px] lg:grid-cols-[0.95fr_1.05fr]"
      >
        <aside className="relative hidden min-h-full overflow-hidden bg-[#142342] lg:block">
          <img src={AuthArtwork} alt="Learners studying together" className="absolute inset-0 h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#101a32] via-[#142342]/35 to-[#142342]/10" />
          <div className="relative flex h-full min-h-[680px] flex-col justify-between p-10 xl:p-14">
            <img src={Logo} alt="Fillop" className="h-14 w-14 rounded-2xl object-cover p-1 shadow-lg" />
            <div className="max-w-md text-white">
              <span className="mb-4 inline-flex rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">Fillop learning</span>
              <h2 className="text-4xl font-bold leading-tight xl:text-5xl">Make your next skill your strongest one.</h2>
              <p className="mt-4 max-w-sm text-base leading-7 text-white/80">Create your account and find courses, resources, and a learning path that fits your goals.</p>
            </div>
            <p className="text-xs text-white/70">Start learning with Fillop today.</p>
          </div>
        </aside>

        <div className="flex items-center justify-center px-5 py-8 sm:px-10 lg:px-12 xl:px-16">
        <div className="w-full max-w-md">

        {/* Logo */}
        <div className="mb-6 flex items-center gap-3 lg:hidden">
          <img src={Logo} alt="Fillop" className="h-11 w-11 rounded-xl object-cover" />
          <span className="font-bold text-slate-900 dark:text-white">Fillop LMS</span>
        </div>

        {/* Header */}
        <div className="mb-5 text-left">
          <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white">Create Account</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Join us and start learning today</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">

          {/* First Name */}
          <div className="relative">
            <User className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
            <input {...register("first_name")} type="text" placeholder="First Name" className={inputCls} />
            {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name.message}</p>}
          </div>

          {/* Last Name */}
          <div className="relative">
            <User className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
            <input {...register("last_name")} type="text" placeholder="Last Name" className={inputCls} />
            {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name.message}</p>}
          </div>

          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
            <input {...register("email")} type="email" placeholder="Email Address" className={inputCls} />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
            <input {...register("password")} type={showPassword ? "text" : "password"} placeholder="Password" className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
            <input {...register("confirmPassword")} type={showConfirm ? "text" : "password"} placeholder="Confirm Password" className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowConfirm(p => !p)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 ${
              loading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
            } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Registering...</> : "Register"}
          </button>

          {/* Login link */}
          <div className="text-center mt-2">
            <p onClick={() => navigate(returnTo ? `/login?next=${encodeURIComponent(returnTo)}` : "/login")} className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm cursor-pointer">
              Already have an account? Sign in
            </p>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            By creating an account, you agree to our{" "}
            <a href="/terms#using-filloptech" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              Terms of Use
            </a>
          </p>

        </form>
        </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Register;
